import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpService } from './http.service';
import { environment } from '../environments/environment';
import { HttpParams } from '@angular/common/http';

// --- Response Types ---

export interface EnrichmentStats {
  statusCounts: Record<string, number>;
  recentJobs: EnrichmentJobSummary[];
  totalCreditsUsed: number;
}

export interface EnrichmentJobSummary {
  id: string;
  provider: string;
  status: string;
  totalContacts: number;
  enrichedContacts: number;
  awaitingReviewContacts: number;
  creditsUsed: number;
  createdAt: string;
  completedAt: string;
}

export interface EnrichmentJob {
  id: string;
  provider: string;
  status: string;
  totalContacts: number;
  processedContacts: number;
  enrichedContacts: number;
  skippedContacts: number;
  failedContacts: number;
  awaitingReviewContacts: number;
  creditsUsed: number;
  progressPercent: number;
  errors: EnrichmentJobError[];
  createdAt: string;
  startedAt: string;
  completedAt: string;
}

export interface EnrichmentJobError {
  contactName: string;
  email: string;
  errorMessage: string;
  timestamp: string;
}

export interface EnrichmentCandidate {
  id: string;
  contactId: string;
  matchScore: number;
  pdlId: string;
  status: string;
  createdAt: string;
  reviewedAt: string;
  candidateData: CandidateData;
}

export interface CandidateData {
  fullName: string;
  firstName: string;
  lastName: string;
  email: string;
  personalEmail: string;
  mobilePhone: string;
  company: string;
  jobTitle: string;
  linkedInUrl: string;
  location: string;
  country: string;
}

export interface DiscoverSearchCriteria {
  jobTitle?: string;
  role?: string | null;
  seniority?: string | null;
  company?: string;
  industry?: string | null;
  companySize?: string | null;
  country?: string | null;
  city?: string;
  skills?: string;
  mustHaveEmail: boolean;
  mustHavePhone: boolean;
  maxResults: number;
  // True when the form was prefilled from a contact and the user is typing
  // into free-text inputs rather than picking PDL enum values. Backend
  // switches role/seniority/etc. to substring matches in this mode.
  freeTextMode?: boolean;
}

export interface DiscoverResult {
  pdlId: string;
  firstName: string;
  lastName: string;
  fullName: string;
  jobTitle: string;
  company: string;
  industry: string;
  linkedInUrl: string;
  location: string;
  email: string;
  mobilePhone: string;
  selected?: boolean;
  /** Set when this PDL hit matches an existing contact (by email or LinkedIn). */
  existingContactId?: string;
  /** Reason the row was flagged: "emailMatch" | "linkedInMatch". */
  duplicateReason?: string;
}

export interface DiscoverSearchResponse {
  total: number;
  results: DiscoverResult[];
}

@Injectable({
  providedIn: 'root',
})
export class EnrichmentService {
  private readonly http = inject(HttpService);
  private readonly baseUrl = `${environment.endPoint}/admin/contacts/enrich`;

  /** Get dashboard statistics (status counts, recent jobs, credits used). */
  getStats(): Observable<EnrichmentStats> {
    return this.http.get<EnrichmentStats>(`${this.baseUrl}/stats`);
  }

  /** Enrich a single contact via PDL. */
  enrichContact(
    contactId: string,
    provider: string = 'PDL',
  ): Observable<{ jobId: string; message: string; status: string }> {
    return this.http.post(`${this.baseUrl}`, { contactId, provider });
  }

  /**
   * Run the PDL Identify (fuzzy match) API for a single contact.
   * Used after Enrich returned AWAITING_IDENTIFY (no direct match) — explicit
   * second step under admin control rather than automatic fall-through.
   */
  identifyContact(
    contactId: string,
  ): Observable<{ jobId: string; message: string; status: string }> {
    return this.http.post(`${this.baseUrl}/contacts/${contactId}/identify`, {});
  }

  /** Trigger initial batch enrichment for all READY contacts. */
  triggerInitialBatch(): Observable<{
    jobId: string;
    message: string;
    status: string;
  }> {
    return this.http.post(`${this.baseUrl}/batch/initial`, {});
  }

  /** Get enrichment job status by ID. */
  getJobStatus(jobId: string): Observable<EnrichmentJob> {
    return this.http.get<EnrichmentJob>(`${this.baseUrl}/status/${jobId}`);
  }

  /** Get enrichment job history. */
  getJobHistory(limit: number = 10): Observable<EnrichmentJob[]> {
    const params = new HttpParams().set('limit', limit.toString());
    return this.http.get<EnrichmentJob[]>(`${this.baseUrl}/history`, params);
  }

  /** List candidates for a contact (fuzzy matches). */
  getCandidates(contactId: string): Observable<EnrichmentCandidate[]> {
    return this.http.get<EnrichmentCandidate[]>(`${this.baseUrl}/contacts/${contactId}/candidates`);
  }

  /** Select a candidate and apply its data to the contact. */
  selectCandidate(
    contactId: string,
    candidateId: string,
  ): Observable<{
    message: string;
    contactId: string;
    enrichmentStatus: string;
  }> {
    return this.http.post(
      `${this.baseUrl}/contacts/${contactId}/candidates/${candidateId}/select`,
      {},
    );
  }

  /** Dismiss all candidates for a contact (sets status to NOT_FOUND). */
  dismissCandidates(contactId: string): Observable<{ message: string; contactId: string }> {
    return this.http.post(`${this.baseUrl}/contacts/${contactId}/dismiss`, {});
  }

  /** Skip enrichment for a contact (sets status to SKIPPED). */
  skipContact(contactId: string): Observable<{ message: string; contactId: string }> {
    return this.http.post(`${this.baseUrl}/contacts/${contactId}/skip`, {});
  }

  /** Search PDL for persons matching criteria (Discover). */
  discoverSearch(criteria: DiscoverSearchCriteria): Observable<DiscoverSearchResponse> {
    return this.http.post<DiscoverSearchResponse>(`${this.baseUrl}/discover/search`, criteria);
  }

  /** Import selected persons from discover search as contacts. */
  discoverImport(persons: DiscoverResult[]): Observable<{
    message: string;
    imported: number;
    skippedDuplicate: number;
    failed: number;
    total: number;
  }> {
    // Map frontend results to PDL PersonData format expected by backend
    const mapped = persons.map((p) => ({
      first_name: p.firstName,
      last_name: p.lastName,
      full_name: p.fullName,
      id: p.pdlId,
      job_title: p.jobTitle,
      job_company_name: p.company,
      job_company_industry: p.industry,
      linkedin_url: p.linkedInUrl,
      location_country: p.location,
      work_email: p.email || false,
      mobile_phone: p.mobilePhone || false,
    }));
    return this.http.post(`${this.baseUrl}/discover/import`, {
      persons: mapped,
    });
  }
}
